<template>
  <div v-if="actions && actions.length > 0" ref="rootRef">
    <AwDropdownMenu
      :content="{ side: 'bottom', align: 'end' }"
      :items="dropdownItems"
      @update:open="handleDropdownOpenChange"
    >
      <AwButton
        :variant="isRowHovered ? 'outline' : 'ghost'"
        color="secondary"
        size="sm"
        leading-icon="i-ph-dots-three-bold"
      />
      <template #demo>
        <slot name="demo"></slot>
      </template>
    </AwDropdownMenu>
  </div>
</template>

<script setup lang="ts" generic="T extends RowData">
import type { RowData } from "@tanstack/table-core";
import type { AwTableRow } from "@asap/design";

export type TableRowAction = {
  label?: string;
  icon?: string;
  color?: "primary" | "critical";
  variant?: "outline" | "ghost" | "solid";
  disabled?: boolean;
  description?: string;
  onClick?: () => void;
};

type Props = {
  row?: AwTableRow<T>;
  actions?: TableRowAction[];
};

const props = defineProps<Props>();

defineSlots<{
  demo?: () => any;
}>();

const rootRef = ref<HTMLElement>();
const isDropdownOpen = ref(false);
const isRowHovered = ref(props.row?.getIsHovered() ?? true);

const TABLE_ROW_HOVER_CLASS = "bg-gray-50";

const getTableRowElement = () => rootRef.value?.closest("tr") ?? null;

const syncTableRowHoverAppearance = () => {
  const tableRowElement = getTableRowElement();
  if (!tableRowElement) {
    return;
  }

  const shouldShowHoverAppearance = isDropdownOpen.value || isRowHovered.value;

  tableRowElement.classList.toggle(TABLE_ROW_HOVER_CLASS, shouldShowHoverAppearance);
};

const pinRowHover = () => {
  if (props.row && !props.row.getIsHovered()) {
    props.row.toggleHovered();
  }
  syncTableRowHoverAppearance();
};

const unpinRowHover = () => {
  nextTick(() => {
    const tableRowElement = getTableRowElement();
    const isMouseOverRow = tableRowElement?.matches(":hover") ?? false;

    if (props.row && !isMouseOverRow && props.row.getIsHovered()) {
      props.row.toggleHovered();
    }

    syncTableRowHoverAppearance();
  });
};

watchEffect(() => {
  isRowHovered.value = props.row?.getIsHovered() ?? true;

  if (isDropdownOpen.value && props.row && !isRowHovered.value) {
    props.row.toggleHovered();
  }

  syncTableRowHoverAppearance();
});

const handleDropdownOpenChange = (open: boolean) => {
  isDropdownOpen.value = open;

  if (open) {
    pinRowHover();
    return;
  }

  unpinRowHover();
};

onBeforeUnmount(() => {
  if (isDropdownOpen.value) {
    isDropdownOpen.value = false;
    unpinRowHover();
  }
});

const dropdownItems = computed(() =>
  (props.actions ?? []).map((action) => ({
    ...action,
    helperText: action.disabled && action.description ? action.description : undefined,
  }))
);
</script>
